/**
 * OnePagerMobileView
 * Mobile-specific view for KPI One Pager V2.
 * 
 * Features:
 * - Expandable Report Builder with metric selection
 * - View selector (Region/Unit, Market/POD, Location)
 * - Maximum 3 metric selection with validation
 * - Mobile-optimized card layout for data display
 */

import React, { useState, useCallback } from 'react';
import {
	Platform,
	View,
	Text,
	StyleSheet,
	ScrollView,
	ActivityIndicator,
	Modal,
	TouchableOpacity,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../../../../theme/useTheme';
import {
	PAGER_COLUMNS,
	SPARC_NAMES,
	SPARC_ORDER,
	type GroupedRow,
	type PagerColumn,
	type SparcCategory,
} from '../../constants/onePagerConstants';
import { formatKpiValue } from '../../utils/onePagerFormatters';
import { isMetricBad } from '../../utils/onePagerStatus';
import ReportBuilder, { type ViewType, type MetricDefinition, type ViewTab } from './ReportBuilder';
import OnePagerMetricCard, { type OnePagerMetricItem } from './OnePagerMetricCard';
import { useOnePagerMobile, mobileViewToGrainKey } from '../../hooks/useOnePagerMobile';
import type { OnePagerFilters } from '../../types/onePagerTypes';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OnePagerMobileViewProps {
	readonly filters: OnePagerFilters;
	readonly columns: PagerColumn[];
}

interface ModalMetricGroup {
	readonly sparc: SparcCategory;
	readonly label: string;
	readonly metrics: OnePagerMetricItem[];
}

// ─── Metric Definitions ───────────────────────────────────────────────────────

/** Available metrics for mobile view */
const MOBILE_METRICS: MetricDefinition[] = [
	{ id: 'rir', label: 'RIR' },
	{ id: 'ttp', label: 'Time to Punch' },
	{ id: 'gap', label: 'Gap Time (Min / Operator)' },
	{ id: 'ontime', label: 'On Time' },
	{ id: 'bpa', label: 'BPA' },
	{ id: 'ihc', label: 'IHC' },
	{ id: 'kcph', label: 'KCPH' },
	{ id: 'haul', label: 'Haulback' },
	{ id: 'repack', label: 'Repack' },
	{ id: 'ot', label: 'Overtime' },
	{ id: 'turn', label: 'Turnover' },
	{ id: 'fill', label: 'Fill Rate' },
	{ id: 'waste', label: 'All in Waste' },
	{ id: 'layer', label: 'Layer' },
	{ id: 'pte_execution', label: 'Perfect Truck Execution' },
	{ id: 'pte', label: 'PTE %' },
];

// ─── View Configuration ───────────────────────────────────────────────────────

const VIEW_TABS: ViewTab[] = [
	{ id: 'region' as ViewType, label: 'Region / Unit' },
	{ id: 'market' as ViewType, label: 'Market / POD' },
	{ id: 'location' as ViewType, label: 'Location' },
];

const MAX_METRICS = 3;
const DEFAULT_SELECTED_METRICS = ['rir', 'bpa', 'ttp'];

// ─── Metric Colors ────────────────────────────────────────────────────────────

/**
 * Metric value colours, matching the desktop table exactly:
 * black when at or better than target, red when not. There is deliberately no
 * "good" green — meeting target is the neutral default.
 */
const METRIC_COLOR_DEFAULT = '#2D2D2D';
const METRIC_COLOR_BAD = '#C12C01';

const SPARC_TITLE_COLORS: Record<SparcCategory, string> = {
	S: '#C43A17',
	P: '#5B48C5',
	A: '#0B5FC6',
	R: '#19759A',
	C: '#0D7A39',
	N: '#f5f6f8',
};

const SPARC_BG_COLORS: Record<SparcCategory, string> = {
	S: 'rgb(255, 231, 225)',
	P: 'rgb(236, 231, 255)',
	A: 'rgb(224, 239, 255)',
	R: 'rgb(220, 235, 240)',
	C: 'rgb(226, 248, 224)',
	N: '#FFFFFF',
};
// ─── Component ────────────────────────────────────────────────────────────────

const OnePagerMobileView: React.FC<OnePagerMobileViewProps> = ({
	filters,
	columns,
}) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
	const {
		content,
		rows,
		loading,
		error,
		selectedViewBy,
		onViewByChange,
	} = useOnePagerMobile({ filters, enabled: true });

	const [isBuilderExpanded, setIsBuilderExpanded] = useState(false);
	const [selectedMetrics, setSelectedMetrics] = useState<string[]>(DEFAULT_SELECTED_METRICS);
	const [appliedMetrics, setAppliedMetrics] = useState<string[]>(DEFAULT_SELECTED_METRICS);
	const [appliedView, setAppliedView] = useState<ViewType>(selectedViewBy);
	const [selectedCard, setSelectedCard] = useState<{
		title: string;
		subtitle: string;
		groupedMetrics: ModalMetricGroup[];
	} | null>(null);
	const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);

	// ─── Handlers ─────────────────────────────────────────────────────────

	const handleToggleBuilder = useCallback(() => {
		setIsBuilderExpanded((prev) => !prev);
	}, []);

	const handleViewChange = useCallback((view: ViewType) => {
		onViewByChange(view);
		setAppliedView(view);
		setIsBuilderExpanded(false);
	}, [onViewByChange]);

	const handleMetricToggle = useCallback((metricId: string) => {
		setSelectedMetrics((prev) => {
			if (prev.includes(metricId)) {
				// Deselect
				return prev.filter((id) => id !== metricId);
			}
			if (prev.length >= MAX_METRICS) {
				return prev;
			}
			return [...prev, metricId];
		});
	}, []);

	const handleReset = useCallback(() => {
		setSelectedMetrics([]);
		onViewByChange('region');
		setAppliedView('region');
	}, [onViewByChange]);

	const handleApply = useCallback(() => {
		setAppliedMetrics(selectedMetrics);
		setAppliedView(selectedViewBy);
		setIsBuilderExpanded(false);
	}, [selectedMetrics, selectedViewBy]);

	const buildModalMetricGroups = useCallback((row: GroupedRow): ModalMetricGroup[] => {
		const grouped: Record<SparcCategory, OnePagerMetricItem[]> = {
			S: [],
			P: [],
			A: [],
			R: [],
			C: [],
			N: [],
		};

		PAGER_COLUMNS.forEach((column) => {
			const formattedValue = formatKpiValue(row.vals[column.id], column);
			const isBad = isMetricBad(row, column.id);

			grouped[column.sparc].push({
				label: column.label,
				value: formattedValue,
				valueColor: isBad ? METRIC_COLOR_BAD : METRIC_COLOR_DEFAULT,
			});
		});

		return SPARC_ORDER.map((sparc) => ({
			sparc,
			label: SPARC_NAMES[sparc],
			metrics: grouped[sparc],
		})).filter((group) => group.metrics.length > 0);
	}, []);

	const handleCardPress = useCallback((title: string, subtitle: string, row: GroupedRow) => {
		setSelectedCard({
			title,
			subtitle,
			groupedMetrics: buildModalMetricGroups(row),
		});
		setIsDetailModalVisible(true);
	}, [buildModalMetricGroups]);

	const handleCloseDetailModal = useCallback(() => {
		setIsDetailModalVisible(false);
		setSelectedCard(null);
	}, []);

	// ─── Render Helpers ───────────────────────────────────────────────────

	const renderDataCards = () => {
		if (loading) {
			return (
				<View style={[styles.loadingContainer, isDark && { backgroundColor: colors.background.surface }]}>
					<ActivityIndicator size="large" color="#005CBC" />
					<Text style={[styles.loadingText, isDark && { color: colors.text.secondary }]}>Loading data...</Text>
				</View>
			);
		}

		if (error) {
			return (
				<View style={[styles.emptyContainer, isDark && { backgroundColor: colors.background.surface }]}>
					<MaterialIcons name="error-outline" size={48} color="#C12C01" />
					<Text style={[styles.emptyText, { color: '#C12C01' }]}>{error}</Text>
				</View>
			);
		}

		if (appliedMetrics.length === 0) {
			return (
				<View style={[styles.emptyContainer, isDark && { backgroundColor: colors.background.surface }]}>
					<MaterialIcons name="insights" size={48} color={isDark ? colors.text.disabled : '#ADB5BD'} />
					<Text style={[styles.emptyText, isDark && { color: colors.text.secondary }]}>
						Please select metrics from the Report Builder above
					</Text>
				</View>
			);
		}

		if (content.length === 0 || rows.length === 0) {
			return (
				<View style={[styles.emptyContainer, isDark && { backgroundColor: colors.background.surface }]}>
					<MaterialIcons name="table-chart" size={48} color={isDark ? colors.text.disabled : '#ADB5BD'} />
					<Text style={[styles.emptyText, isDark && { color: colors.text.secondary }]}>
						No data available for the current configuration
					</Text>
				</View>
			);
		}

		// Get the grain key for the applied view
		const grainKey = mobileViewToGrainKey(appliedView);

		return (
			<View style={styles.cardsContainer}>
				{content.map((contentItem, index) => {
					const row = rows[index];
					if (!row) return null;

					const drill = Array.isArray(contentItem.drillPath) ? contentItem.drillPath[0] : undefined;
					const grainValue = drill?.value || row.grainVals[grainKey] || 'Unknown';
					const secondaryLabel = drill?.field || '';

					const cardMetrics = appliedMetrics
						.map((metricId) => {
							const column = columns.find((col) => col.id === metricId);
							const rawValue = row.vals[metricId];

							if (!column) {
								return {
									label: metricId.toUpperCase(),
									value: typeof rawValue === 'number' ? rawValue.toFixed(2) : '--',
									valueColor: METRIC_COLOR_DEFAULT,
								};
							}

							const formattedValue = formatKpiValue(rawValue, column);
							// Same backend-driven rule as the desktop table: red only when the
							// API flags the metric as not meeting target. Previously this
							// recomputed thresholds locally against the hardcoded column.target
							// and coloured passing values green, so mobile could disagree with
							// desktop for the very same row.
							const isBad = isMetricBad(row, column.id);

							return {
								label: column.label,
								value: formattedValue,
								valueColor: isBad ? METRIC_COLOR_BAD : METRIC_COLOR_DEFAULT,
							};
						})
						.filter((metric) => metric != null);
					
					return (
						<OnePagerMetricCard
							key={`${grainValue}-${index}`}
							title={grainValue}
							subtitle={secondaryLabel}
							metrics={cardMetrics}
							onPress={() => handleCardPress(grainValue, secondaryLabel, row)}
						/>
					);
				})}
			</View>
		);
	};

	// ─── Render ───────────────────────────────────────────────────────────

	return (
		<ScrollView
			style={[styles.container, isDark && { backgroundColor: colors.background.page }]}
			contentContainerStyle={styles.scrollContent}
			showsVerticalScrollIndicator={false}
		>
			<ReportBuilder
				isExpanded={isBuilderExpanded}
				selectedView={selectedViewBy}
				selectedMetrics={selectedMetrics}
				availableMetrics={MOBILE_METRICS}
				viewTabs={VIEW_TABS}
				maxMetrics={MAX_METRICS}
				disableUnselectedMetrics
				onToggle={handleToggleBuilder}
				onViewChange={handleViewChange}
				onMetricToggle={handleMetricToggle}
				onReset={handleReset}
				onApply={handleApply}
			/>
			<View style={styles.contentSection}>
				{renderDataCards()}
			</View>

			<Modal
				visible={isDetailModalVisible}
				transparent
				animationType="fade"
				onRequestClose={handleCloseDetailModal}
			>
				<View style={styles.modalOverlay}>
					<View style={[styles.modalCard, isDark && { backgroundColor: colors.background.surface }]}>
						<View style={styles.modalHeader}>
							<View style={styles.modalTitleContainer}>
								<Text style={[styles.modalTitle, isDark && { color: colors.text.primary }]} numberOfLines={1}>
									{selectedCard?.title ?? 'Details'}
								</Text>
								{!!selectedCard?.subtitle && (
									<Text style={[styles.modalSubtitle, isDark && { color: colors.text.secondary }]} numberOfLines={1}>
										{selectedCard.subtitle}
									</Text>
								)}
							</View>
							<TouchableOpacity
								onPress={handleCloseDetailModal}
								style={styles.modalCloseButton}
								accessibilityRole="button"
							>
								<MaterialIcons name="close" size={22} color={isDark ? colors.text.secondary : '#495057'} />
							</TouchableOpacity>
						</View>

						<ScrollView
							style={styles.modalScrollArea}
							contentContainerStyle={styles.modalScrollContent}
							showsVerticalScrollIndicator
						>
							<View style={styles.modalMetricsContainer}>
								{(selectedCard?.groupedMetrics ?? []).map((group) => (
									<View key={group.sparc} style={[styles.modalGroupSection, isDark && { borderBottomColor: colors.border.divider }]}>
										<Text
											style={[
												styles.modalGroupTitle,
												{ color: SPARC_TITLE_COLORS[group.sparc] },
												{backgroundColor: SPARC_TITLE_COLORS[group.sparc] + '1A', paddingHorizontal: 4, borderRadius: 4}
											]}
										>
											{group.label}
										</Text>
										{group.metrics.map((metric, index) => (
											<View
												key={`${group.sparc}-${metric.label}-${index}`}
												style={styles.modalMetricRow}
											>
												<Text style={[styles.modalMetricLabel, isDark && { color: colors.text.secondary }]}>{metric.label}</Text>
												<Text
													style={[
														styles.modalMetricValue,
														metric.valueColor ? { color: metric.valueColor } : null,
														isDark && !metric.valueColor && { color: colors.text.primary },
													]}
												>
													{metric.value}
												</Text>
											</View>
										))}
									</View>
								))}
							</View>
						</ScrollView>
					</View>
				</View>
			</Modal>
		</ScrollView>
	);
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: '#F8F9FA',
	},
	scrollContent: {
		paddingHorizontal: 14,
		paddingTop: 1,
		paddingBottom: 12,
	},
	contentSection: {
		marginTop: 10,
	},
	// Data Cards
	cardsContainer: {
		gap: 10,
	},
	// Loading & Empty States
	loadingContainer: {
		paddingVertical: 24,
		paddingHorizontal: 16,
		alignItems: 'center',
		backgroundColor: '#FFFFFF',
		borderRadius: 8,
	},
	loadingText: {
		marginTop: 12,
		fontSize: 14,
		fontFamily: 'Inter-Regular',
		color: '#6C757D',
	},
	emptyContainer: {
		paddingVertical: 24,
		paddingHorizontal: 16,
		alignItems: 'center',
		backgroundColor: '#FFFFFF',
		borderRadius: 8,
	},
	emptyText: {
		marginTop: 12,
		fontSize: 14,
		fontFamily: 'Inter-Regular',
		color: '#6C757D',
		textAlign: 'center',
	},
	modalOverlay: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.35)',
		justifyContent: 'center',
		alignItems: 'center',
		paddingHorizontal: 18,
	},
	modalCard: {
		width: '100%',
		maxHeight: '88%',
		backgroundColor: '#FFFFFF',
		borderRadius: 14,
		paddingHorizontal: 16,
		paddingVertical: 14,
	},
	modalHeader: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		marginBottom: 10,
	},
	modalTitleContainer: {
		flex: 1,
		paddingRight: 12,
	},
	modalTitle: {
		fontSize: 16,
		fontFamily: 'Inter-Bold',
		fontWeight: '700',
		color: '#212529',
	},
	modalSubtitle: {
		marginTop: 2,
		fontSize: 12,
		fontFamily: 'Inter-Regular',
		color: '#6C757D',
	},
	modalCloseButton: {
		padding: 4,
	},
	modalScrollArea: {
		flexShrink: 1,
		paddingHorizontal: 10,
	},
	modalScrollContent: {
		paddingBottom: 0,
	},
	modalMetricsContainer: {
		marginTop: 4,
	},
	modalGroupSection: {
		marginBottom: 8,
		borderBottomWidth: 1,
		borderBottomColor: '#EEF1F4',
	},
	modalGroupTitle: {
		fontSize: 12,
		fontFamily: 'Inter-Bold',
		fontWeight: '700',
		textTransform: 'uppercase',
		marginTop: 8,
		marginBottom: 2,
		paddingVertical: 5,
		borderRadius: 4,
		overflow: 'hidden',
	},
	modalMetricRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingVertical: 8,
		//borderBottomWidth: 1,
		//borderBottomColor: '#EEF1F4',
	},
	modalMetricLabel: {
		fontSize: 13,
		fontFamily: 'Inter-SemiBold',
		fontWeight: '600',
		color: '#495057',
	},
	modalMetricValue: {
		fontSize: 14,
		fontFamily: 'Inter-Bold',
		fontWeight: '700',
		color: '#212529',
	},
});

OnePagerMobileView.displayName = 'OnePagerMobileView';

export default React.memo(OnePagerMobileView);
